import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus, Trash2, CheckCircle, AlertTriangle, Info, BarChart2, Eye } from "react-feather";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BaseModal from "../../../../BaseComponents/BaseModal";
import ComponentRow from "../../../../BaseComponents/ComponentRow";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";
import BaseInput from "../../../../BaseComponents/BaseInput";
import BaseCheckbox from "../../../../BaseComponents/BaseCheckbox";
import BaseSelect from "../../../../BaseComponents/BaseSelect";
import "./PayrollTemplateBuilder.css";
import {
  detectCycle,
  computePayrollPreview,
  computeAllocation,
  resolveComponentsWithSalary,
} from "../../../../Constant/HR/payrollCalculations";
import {
  templateSchema,
  activatingTemplateSchema,
  validateNoDuplicateNames,
  validateAllocation,
} from "../../../../Constant/HR/payrollValidations";
import {
  createTemplateApi,
  viewTemplateApi,
  editTemplateApi,
  toggleTemplateApi,
  listPtSlabsApi,
  listTdsSlabsApi,
} from "../../../../Api/payrollApi";
import {
  checkStatusCodeSuccess,
  rendorBackButton,
  getLocalItem,
} from "../../../../Constant/common";
import { PAYROLL_STRINGS, DEFAULT_TDS_SLABS } from "../../../../Constant/HR/payrollConstants";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const formatINR = (v) =>
  v == null || isNaN(v) ? "—" : `\u20b9${Number(v).toLocaleString("en-IN")}`;
const currentMonthInFY = () => {
  const m = new Date().getMonth() + 1;
  return m >= 4 ? m - 3 : m + 9;
};

const DEFAULT_COMPONENTS = (() => {
  const basicId = newId();
  return [
    {
      id: basicId,
      name: "Basic Salary",
      calculationType: "percentage_of_gross",
      percentage: 50,
      fixedAmount: 0,
      baseComponentId: null,
      isTaxable: true,
      isProrata: true,
      isResidual: false,
      order: 0,
    },
    {
      id: newId(),
      name: "HRA",
      calculationType: "percentage_of_component",
      percentage: 40,
      fixedAmount: 0,
      baseComponentId: basicId,
      isTaxable: false,
      isProrata: true,
      isResidual: false,
      order: 1,
    },
    {
      id: newId(),
      name: "Conveyance Allowance",
      calculationType: "fixed",
      percentage: 0,
      fixedAmount: 1600,
      baseComponentId: null,
      isTaxable: false,
      isProrata: false,
      isResidual: false,
      order: 2,
    },
    {
      id: newId(),
      name: "Special Allowance",
      calculationType: "residual",
      percentage: 0,
      fixedAmount: 0,
      baseComponentId: null,
      isTaxable: true,
      isProrata: true,
      isResidual: true,
      order: 3,
    },
  ];
})();

const INITIAL_FORM = {
  name: "",
  description: "",
  earningComponents: DEFAULT_COMPONENTS,
  deductionComponents: {
    pf: { enabled: false, employeePercent: 12, employerPercent: 12, ceiling: 15000 },
    esic: {
      enabled: false,
      employeePercent: 0.75,
      employerPercent: 3.25,
      eligibilityCeiling: 21000,
    },
    pt: { enabled: false },
    tds: { enabled: false },
    custom: [],
  },
  assignedRoles: [],
};

const SectionHeading = ({ children, color }) => (
  <div className="tb-section-heading" style={color ? { color } : {}}>
    {children}
  </div>
);

const FieldRow = ({ label, required, children, error }) => (
  <div className="tb-field-row">
    <label>
      {label}
      {required && <span className="tb-field-row-required">*</span>}
    </label>
    {children}
    {error && (
      <div className="tb-field-row-error" role="alert">
        {error}
      </div>
    )}
  </div>
);

const DeductionToggle = ({ label, enabled, onToggle, tooltip, children }) => (
  <div className="tb-deduction-toggle">
    <div className={`tb-deduction-header ${enabled ? "enabled" : ""}`}>
      <BaseCheckbox
        id={`deduction-toggle-${label}`}
        label={
          <span className={`tb-deduction-label-text ${enabled ? "enabled" : ""}`}>{label}</span>
        }
        checked={!!enabled}
        onChange={(e) => onToggle(e.target.checked)}
        checkboxClassName="tb-deduction-checkbox"
        className="tb-deduction-label mb-0"
      />
      {tooltip && (
        <BaseTooltip label={tooltip} position="top">
          <span className="tb-deduction-tooltip-icon">
            <Info size={14} strokeWidth={2.5} />
          </span>
        </BaseTooltip>
      )}
    </div>
    {enabled && children && <div className="tb-deduction-body">{children}</div>}
  </div>
);

const NumInput = ({ label, value, onChange, min, max, step, ariaLabel }) => (
  <div className="tb-num-input-wrapper">
    <BaseInput
      type="number"
      label={label}
      value={value ?? ""}
      min={min}
      max={max}
      step={step}
      aria-label={ariaLabel || label}
      handleChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className="tb-input-sm mb-0"
    />
  </div>
);

const StatCard = ({ label, value, color, sub }) => (
  <div className="tb-stat-card">
    <div className="tb-stat-card-label">{label}</div>
    <div className="tb-stat-card-value" style={color ? { color } : {}}>
      {value}
    </div>
    {sub && <div className="tb-stat-card-sub">{sub}</div>}
  </div>
);

const StructureSummaryCard = ({ components, referenceSalary, allocation }) => {
  const resolved = useMemo(
    () => resolveComponentsWithSalary(components, referenceSalary),
    [components, referenceSalary],
  );
  return (
    <div className="glass rounded-2xl animate-slide-up tb-summary-card">
      <div className="tb-summary-header">
        <BarChart2 size={14} strokeWidth={2} className="tb-summary-icon" />
        <span className="tb-summary-title">Structure Summary</span>
        <span className={`tb-summary-badge ${allocation.isBalanced ? "balanced" : "unbalanced"}`}>
          {allocation.isBalanced ? "Balanced" : `${allocation.totalPercent.toFixed(1)}% allocated`}
        </span>
      </div>
      <div className="tb-summary-list">
        {components.map((c) => {
          const amount = resolved.get(c.id) || 0;
          const pct = referenceSalary > 0 ? (amount / referenceSalary) * 100 : 0;
          const isRes = c.calculationType === "residual" || c.isResidual;
          const baseName = components.find((x) => x.id === c.baseComponentId)?.name || "comp";
          return (
            <div key={c.id} className={`tb-summary-item ${isRes ? "residual" : ""}`}>
              <span className="tb-summary-item-name">{c.name || "(unnamed)"}</span>
              <span className="tb-summary-item-type">
                {isRes
                  ? "Auto-balance"
                  : c.calculationType === "fixed"
                    ? formatINR(c.fixedAmount)
                    : c.calculationType === "percentage_of_gross"
                      ? `${c.percentage}% of salary`
                      : c.calculationType === "percentage_of_component" ||
                          c.calculationType === "percentage"
                        ? `${c.percentage}% of ${baseName}`
                        : `${c.percentage}%`}
              </span>
              <span className={`tb-summary-item-percent ${isRes ? "residual" : ""}`}>
                {isRes ? "~" : `${pct.toFixed(1)}%`}
              </span>
            </div>
          );
        })}
      </div>
      <div className="tb-summary-progress">
        <div
          className={`tb-summary-progress-bar ${
            allocation.isBalanced
              ? "balanced"
              : allocation.totalPercent > 100
                ? "overflow"
                : "unbalanced-warning"
          }`}
          style={{ width: `${Math.min(100, allocation.totalPercent)}%` }}
        />
      </div>
      <div className="tb-summary-scale">
        <span className="tb-summary-scale-label">0%</span>
        <span className="tb-summary-scale-label">100%</span>
      </div>
    </div>
  );
};

const LivePayrollPreview = ({ template, ptSlabs, tdsSlabs }) => {
  const [salary, setSalary] = useState(50000);
  const [presentDays, setPresentDays] = useState(26);
  const [monthDays, setMonthDays] = useState(26);
  const preview = useMemo(() => {
    try {
      return computePayrollPreview(
        template,
        { monthlySalary: salary, presentDays, monthDays },
        {
          ptSlabs: ptSlabs || [],
          tdsSlabs: tdsSlabs || DEFAULT_TDS_SLABS,
          monthInFY: currentMonthInFY(),
        },
      );
    } catch {
      return null;
    }
  }, [template, salary, presentDays, monthDays, ptSlabs, tdsSlabs]);
  return (
    <div className="glass rounded-2xl animate-slide-up tb-preview-card">
      <div className="tb-preview-header">
        <Eye size={14} strokeWidth={2} className="tb-preview-icon" />
        <span className="tb-preview-title">Live Preview</span>
      </div>
      <div className="tb-preview-inputs">
        {[
          { label: "Monthly Salary", value: salary, set: setSalary, min: 1000 },
          { label: "Present Days", value: presentDays, set: setPresentDays, min: 0, max: 31 },
          { label: "Month Days", value: monthDays, set: setMonthDays, min: 1, max: 31 },
        ].map(({ label, value, set, min, max }) => (
          <BaseInput
            key={label}
            type="number"
            label={label}
            value={value}
            min={min}
            max={max}
            handleChange={(e) => set(Number(e.target.value) || 0)}
            className="tb-input-sm mb-0"
          />
        ))}
      </div>
      {preview ? (
        <>
          <div className="tb-preview-cards">
            <StatCard label="Gross" value={formatINR(preview.grossSalary)} color="#6366f1" />
            <StatCard
              label="LOP"
              value={formatINR(preview.lopAmount)}
              color="#f59e0b"
              sub={`${preview.absentDays} absent`}
            />
            <StatCard label="Net Salary" value={formatINR(preview.netSalary)} color="#10b981" />
          </div>
          {(preview.pfEmployee > 0 || preview.esic > 0 || preview.pt > 0 || preview.tds > 0) && (
            <div className="tb-preview-deductions">
              {preview.pfEmployee > 0 && (
                <StatCard label="PF" value={formatINR(preview.pfEmployee)} color="#f43f5e" />
              )}
              {preview.esic > 0 && (
                <StatCard label="ESIC" value={formatINR(preview.esic)} color="#f43f5e" />
              )}
              {preview.pt > 0 && (
                <StatCard label="PT" value={formatINR(preview.pt)} color="#f43f5e" />
              )}
              {preview.tds > 0 && (
                <StatCard label="TDS" value={formatINR(preview.tds)} color="#f43f5e" />
              )}
            </div>
          )}
          <div className="tb-preview-breakdown">
            <div className="tb-preview-breakdown-title">Earnings Breakdown</div>
            {preview.earnings.map((e) => (
              <div key={e.id} className="tb-preview-breakdown-item">
                <span className="tb-preview-breakdown-name">
                  {e.name}
                  {e.isResidual && <span className="tb-preview-breakdown-badge">residual</span>}
                  {!e.isProrata && <span className="tb-preview-breakdown-badge fixed">fixed</span>}
                </span>
                <span className="tb-preview-breakdown-amount">{formatINR(e.amount)}</span>
              </div>
            ))}
            <div className="tb-preview-gross">
              <span className="tb-preview-gross-label">Gross</span>
              <span className="tb-preview-gross-value">{formatINR(preview.grossSalary)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="tb-preview-empty">Add earning components to see preview</div>
      )}
    </div>
  );
};

const ValidationSummary = ({ errors, cycleError, duplicateError, allocationError }) => {
  const allErrors = [
    cycleError,
    duplicateError,
    allocationError,
    ...Object.values(errors).filter(Boolean),
  ].filter(Boolean);
  if (allErrors.length === 0) {
    return (
      <div className="tb-validation-success">
        <CheckCircle size={14} strokeWidth={2} className="tb-validation-success-icon" />
        <span className="tb-validation-success-text">Structure is valid</span>
      </div>
    );
  }
  return (
    <div className="tb-validation-errors">
      <div className="tb-validation-errors-header">
        <AlertTriangle size={13} strokeWidth={2.5} className="tb-validation-errors-icon" />
        <span className="tb-validation-errors-title">
          {allErrors.length} issue{allErrors.length !== 1 ? "s" : ""}
        </span>
      </div>
      <ul className="tb-validation-errors-list">
        {allErrors.map((err, i) => (
          <li key={i} className="tb-validation-errors-item">
            {err}
          </li>
        ))}
      </ul>
    </div>
  );
};

const TemplateBuilder = ({ isOnboarding = false, onSuccess }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const REFERENCE_SALARY = 50000;

  const [formValues, setFormValues] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [cycleError, setCycleError] = useState(null);
  const [duplicateError, setDuplicateError] = useState(null);
  const [allocationError, setAllocationError] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [showUsedWarning, setShowUsedWarning] = useState(false);
  const [pendingActivate, setPendingActivate] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setIsLoadingTemplate(true);
    viewTemplateApi(id)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode) && resp?.data) {
          const t = resp.data;
          const rawEarnings = Array.isArray(t.earningComponents) ? t.earningComponents : [];
          const earningComponents = rawEarnings.map((c) => {
            if (c.baseComponentId) {
              const match = rawEarnings.find(
                (other) =>
                  other.id !== c.id &&
                  (other.name === c.baseComponentId ||
                    String(other.id) === String(c.baseComponentId)),
              );
              if (match) {
                return { ...c, baseComponentId: match.id };
              }
            }
            return c;
          });
          setFormValues({
            name: t.name || "",
            description: t.description || "",
            earningComponents,
            deductionComponents: t.deductionComponents || INITIAL_FORM.deductionComponents,
            assignedRoles: Array.isArray(t.assignedRoles) ? t.assignedRoles : [],
          });
        } else {
          toast.error(PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch(() => toast.error(PAYROLL_STRINGS.ERROR_GENERIC))
      .finally(() => setIsLoadingTemplate(false));
  }, [id, isEdit]);

  useEffect(() => {
    setCycleError(
      detectCycle(formValues.earningComponents) ? PAYROLL_STRINGS.ERROR_CIRCULAR_DEPENDENCY : null,
    );
    setDuplicateError(validateNoDuplicateNames(formValues.earningComponents));
    setAllocationError(validateAllocation(formValues.earningComponents, REFERENCE_SALARY));
  }, [formValues.earningComponents]);

  const allocation = useMemo(
    () => computeAllocation(formValues.earningComponents, REFERENCE_SALARY),
    [formValues.earningComponents],
  );
  const componentAllocations = useMemo(() => {
    const resolved = resolveComponentsWithSalary(formValues.earningComponents, REFERENCE_SALARY);
    const map = {};
    formValues.earningComponents.forEach((c) => {
      map[c.id] = REFERENCE_SALARY > 0 ? ((resolved.get(c.id) || 0) / REFERENCE_SALARY) * 100 : 0;
    });
    return map;
  }, [formValues.earningComponents]);

  const setField = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n[field];
      return n;
    });
  }, []);

  const handleAddComponent = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      earningComponents: [
        ...prev.earningComponents,
        {
          id: newId(),
          name: "",
          calculationType: "percentage_of_gross",
          fixedAmount: 0,
          percentage: 0,
          baseComponentId: null,
          isTaxable: true,
          isProrata: true,
          isResidual: false,
          order: prev.earningComponents.length,
        },
      ],
    }));
  }, []);

  const handleUpdateComponent = useCallback((compId, field, value) => {
    setFormValues((prev) => ({
      ...prev,
      earningComponents: prev.earningComponents.map((c) =>
        c.id === compId ? { ...c, [field]: value } : c,
      ),
    }));
  }, []);

  const handleRemoveComponent = useCallback((compId) => {
    setFormValues((prev) => ({
      ...prev,
      earningComponents: prev.earningComponents.filter((c) => c.id !== compId),
    }));
  }, []);

  const handleSetResidual = useCallback((compId) => {
    setFormValues((prev) => ({
      ...prev,
      earningComponents: prev.earningComponents.map((c) => ({
        ...c,
        isResidual: c.id === compId ? !c.isResidual : false,
        calculationType:
          c.id === compId
            ? c.isResidual
              ? "percentage_of_gross"
              : "residual"
            : c.calculationType === "residual"
              ? "percentage_of_gross"
              : c.calculationType,
      })),
    }));
  }, []);

  const handleDragStart = useCallback((index) => setDragIndex(index), []);
  const handleDragOver = useCallback((e) => e.preventDefault(), []);
  const handleDrop = useCallback(
    (dropIndex) => {
      if (dragIndex === null || dragIndex === dropIndex) return;
      setFormValues((prev) => {
        const updated = [...prev.earningComponents];
        const [moved] = updated.splice(dragIndex, 1);
        updated.splice(dropIndex, 0, moved);
        return { ...prev, earningComponents: updated };
      });
      setDragIndex(null);
    },
    [dragIndex],
  );

  const updateDeduction = useCallback((key, patch) => {
    setFormValues((prev) => ({
      ...prev,
      deductionComponents: {
        ...prev.deductionComponents,
        [key]: { ...prev.deductionComponents[key], ...patch },
      },
    }));
  }, []);

  const handleTogglePt = useCallback(
    (enable) => {
      if (!enable) {
        updateDeduction("pt", { enabled: false });
        return;
      }
      const orgId = getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
      listPtSlabsApi(orgId)
        .then((resp) => {
          const slabs = resp?.data ?? resp;
          if (Array.isArray(slabs) && slabs.length > 0) {
            updateDeduction("pt", { enabled: true });
          } else {
            toast.warning(
              "Please configure Professional Tax slabs first at /payrollSettings/ptSlabs",
            );
            updateDeduction("pt", { enabled: false });
          }
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to check Professional Tax slabs.",
          );
          updateDeduction("pt", { enabled: false });
        });
    },
    [updateDeduction],
  );

  const handleToggleTds = useCallback(
    (enable) => {
      if (!enable) {
        updateDeduction("tds", { enabled: false });
        return;
      }
      const orgId = getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
      listTdsSlabsApi(orgId)
        .then((resp) => {
          const slabs = resp?.data ?? resp;
          if (Array.isArray(slabs) && slabs.length > 0) {
            updateDeduction("tds", { enabled: true });
          } else {
            toast.warning("Please configure TDS slabs first at /payrollSettings/tdsSlabs");
            updateDeduction("tds", { enabled: false });
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message || "Failed to check TDS slabs.");
          updateDeduction("tds", { enabled: false });
        });
    },
    [updateDeduction],
  );

  const handleAddCustomDeduction = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      deductionComponents: {
        ...prev.deductionComponents,
        custom: [
          ...(prev.deductionComponents.custom || []),
          { id: newId(), name: "", calculationType: "fixed", fixedAmount: 0, percentage: 0 },
        ],
      },
    }));
  }, []);

  const handleUpdateCustomDeduction = useCallback((dedId, field, value) => {
    setFormValues((prev) => ({
      ...prev,
      deductionComponents: {
        ...prev.deductionComponents,
        custom: prev.deductionComponents.custom.map((d) =>
          d.id === dedId ? { ...d, [field]: value } : d,
        ),
      },
    }));
  }, []);

  const handleRemoveCustomDeduction = useCallback((dedId) => {
    setFormValues((prev) => ({
      ...prev,
      deductionComponents: {
        ...prev.deductionComponents,
        custom: prev.deductionComponents.custom.filter((d) => d.id !== dedId),
      },
    }));
  }, []);

  const getAvailableBases = useCallback(
    (currentId) =>
      formValues.earningComponents
        .filter((c) => c.id !== currentId && c.calculationType !== "residual" && !c.isResidual)
        .map((c) => ({ id: c.id, name: c.name || "(unnamed)" })),
    [formValues.earningComponents],
  );

  const validate = useCallback(
    async (activating = false) => {
      const schema = activating ? activatingTemplateSchema : templateSchema;
      try {
        await schema.validate(formValues, { abortEarly: false });
        setErrors({});
      } catch (err) {
        if (err.name === "ValidationError") {
          const errs = {};
          for (const ve of err.inner) {
            if (ve.path && !(ve.path in errs)) errs[ve.path] = ve.message;
          }
          setErrors(errs);
          return false;
        }
      }
      if (cycleError || duplicateError) return false;
      if (activating && allocationError) return false;
      return true;
    },
    [formValues, cycleError, duplicateError, allocationError],
  );

  const doSave = useCallback(
    async (activating = false) => {
      const valid = await validate(activating);
      if (!valid) {
        toast.error("Please fix validation errors before saving.");
        return false;
      }
      const payload = {
        name: formValues.name,
        description: formValues.description,
        earningComponents: formValues.earningComponents,
        deductionComponents: formValues.deductionComponents,
        assignedRoles: formValues.assignedRoles,
      };
      activating ? setIsActivating(true) : setIsSaving(true);
      try {
        const resp = isEdit ? await editTemplateApi(id, payload) : await createTemplateApi(payload);
        if (!checkStatusCodeSuccess(resp?.statusCode)) {
          toast.error(resp?.message || PAYROLL_STRINGS.ERROR_GENERIC);
          return false;
        }
        const savedId = resp?.data?.id || id;
        if (activating && savedId) {
          const toggleResp = await toggleTemplateApi(savedId);
          toast.success(
            checkStatusCodeSuccess(toggleResp?.statusCode)
              ? PAYROLL_STRINGS.SUCCESS_TEMPLATE_ACTIVATED
              : isEdit
                ? PAYROLL_STRINGS.SUCCESS_TEMPLATE_UPDATED
                : PAYROLL_STRINGS.SUCCESS_TEMPLATE_CREATED,
          );
        } else {
          toast.success(
            isEdit
              ? PAYROLL_STRINGS.SUCCESS_TEMPLATE_UPDATED
              : PAYROLL_STRINGS.SUCCESS_TEMPLATE_CREATED,
          );
        }
        if (isOnboarding && onSuccess) {
          onSuccess(savedId);
        } else {
          navigate("/payrollTemplates");
        }
        return true;
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || PAYROLL_STRINGS.ERROR_GENERIC);
        return false;
      } finally {
        setIsSaving(false);
        setIsActivating(false);
      }
    },
    [validate, formValues, isEdit, id, navigate, isOnboarding, onSuccess],
  );

  const handleSaveDraft = useCallback(() => doSave(false), [doSave]);
  const handlePublish = useCallback(async () => {
    if (isEdit && formValues._hasActiveBatches) {
      setShowUsedWarning(true);
      setPendingActivate(true);
      return;
    }
    doSave(true);
  }, [doSave, isEdit, formValues._hasActiveBatches]);
  const handleValidate = useCallback(async () => {
    setIsValidating(true);
    await validate(true);
    setIsValidating(false);
    const hasErrors =
      cycleError || duplicateError || allocationError || Object.keys(errors).length > 0;
    if (!hasErrors) toast.success("Structure is valid");
  }, [validate, cycleError, duplicateError, allocationError, errors]);
  const handleConfirmUsedWarning = useCallback(async () => {
    setShowUsedWarning(false);
    await doSave(pendingActivate);
    setPendingActivate(false);
  }, [doSave, pendingActivate]);

  const isBusy = isSaving || isActivating;
  const { earningComponents, deductionComponents } = formValues;
  const { pf, esic, pt, tds, custom = [] } = deductionComponents;
  const hasAnyError = !!(
    cycleError ||
    duplicateError ||
    allocationError ||
    Object.keys(errors).length
  );

  if (isLoadingTemplate) {
    return (
      <div className="tb-loading-container">
        <span className="ds-spinner" role="status" aria-label="Loading template" />
        <p className="tb-loading-text">Loading template...</p>
      </div>
    );
  }

  return (
    <div className={`animate-fade-in tb-page-wrapper ${isOnboarding ? "onboarding" : ""}`}>
      {!isOnboarding && (
        <div className="animate-slide-down d-flex align-items-center justify-content-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="page-header-title">
              {isEdit ? PAYROLL_STRINGS.BUILDER_TITLE_EDIT : PAYROLL_STRINGS.BUILDER_TITLE_NEW}
            </h1>
            <p className="page-header-subtitle">
              One payroll structure applies to all staff — amounts are calculated from each
              employee&apos;s salary
            </p>
          </div>
          {rendorBackButton(() => navigate("/payrollTemplates"))}
        </div>
      )}

      <div className="builder-layout mb-3">
        <div className="d-flex flex-column gap-4">
          <div className="glass rounded-4 animate-slide-up p-4">
            <SectionHeading>Template Details</SectionHeading>
            <FieldRow label="Template Name" required>
              <BaseInput
                name="name"
                type="text"
                value={formValues.name}
                handleChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. Standard Payroll Structure"
                className={errors.name ? "is-invalid" : ""}
                touched={!!errors.name}
                error={errors.name}
              />
            </FieldRow>
            <FieldRow label="Description">
              <BaseInput
                name="description"
                type="text"
                value={formValues.description}
                handleChange={(e) => setField("description", e.target.value)}
                placeholder="Optional description"
                className={errors.description ? "is-invalid" : ""}
                touched={!!errors.description}
                error={errors.description}
              />
            </FieldRow>
          </div>

          <div className="glass rounded-4 animate-slide-up p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <SectionHeading>Earning Components</SectionHeading>
              <BaseButton color="primary" onClick={handleAddComponent}>
                <Plus size={14} strokeWidth={2.5} />
                {PAYROLL_STRINGS.BTN_ADD_COMPONENT}
              </BaseButton>
            </div>
            <div className="p-3 rounded-2 border tb-info-box">
              <strong className="tb-info-box-strong">% of Salary</strong> uses each staff
              member&apos;s <code className="tb-info-box-code">monthly_salary</code> as the base.{" "}
              <strong className="tb-info-box-highlight">Residual</strong> auto-absorbs unallocated
              salary.
            </div>
            {cycleError && (
              <div className="tb-error-box" role="alert">
                {cycleError}
              </div>
            )}
            {duplicateError && (
              <div className="tb-warning-box" role="alert">
                {duplicateError}
              </div>
            )}
            {allocationError && (
              <div className="tb-warning-box" role="alert">
                {allocationError}
              </div>
            )}
            {earningComponents.length === 0 ? (
              <div className="tb-empty-state">
                No earning components yet. Click &quot;Add Component&quot; to start.
              </div>
            ) : (
              <div role="list">
                {earningComponents.map((comp, index) => (
                  <div key={comp.id} role="listitem">
                    <ComponentRow
                      component={comp}
                      index={index}
                      onUpdate={handleUpdateComponent}
                      onRemove={handleRemoveComponent}
                      onSetResidual={handleSetResidual}
                      availableBaseComponents={getAvailableBases(comp.id)}
                      hasCircularError={!!cycleError}
                      allocationPercent={componentAllocations[comp.id] ?? null}
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                      isOnboarding={isOnboarding}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="glass rounded-4 animate-slide-up p-4">
            <SectionHeading>Deduction Components</SectionHeading>
            <DeductionToggle
              label="Provident Fund (PF)"
              enabled={pf.enabled}
              onToggle={(v) => updateDeduction("pf", { enabled: v })}
              tooltip="Provident Fund: A mandatory retirement savings scheme where both the employer and employee contribute (usually 12% of basic salary)."
            >
              <NumInput
                label="Employee PF %"
                value={pf.employeePercent}
                onChange={(v) => updateDeduction("pf", { employeePercent: v })}
                min={0}
                max={100}
                step={0.01}
              />
              <NumInput
                label="Employer PF %"
                value={pf.employerPercent}
                onChange={(v) => updateDeduction("pf", { employerPercent: v })}
                min={0}
                max={100}
                step={0.01}
              />
              <NumInput
                label="PF Wage Ceiling"
                value={pf.ceiling ?? 15000}
                onChange={(v) => updateDeduction("pf", { ceiling: v })}
                min={0}
                step={1000}
                ariaLabel="PF wage ceiling"
              />
            </DeductionToggle>
            <DeductionToggle
              label="ESIC"
              enabled={esic.enabled}
              onToggle={(v) => updateDeduction("esic", { enabled: v })}
              tooltip="Employee State Insurance: A social security and health insurance scheme providing medical benefits for employees earning below ₹21,000/month."
            >
              <NumInput
                label="Employee ESIC %"
                value={esic.employeePercent}
                onChange={(v) => updateDeduction("esic", { employeePercent: v })}
                min={0}
                max={100}
                step={0.01}
              />
              <NumInput
                label="Employer ESIC %"
                value={esic.employerPercent ?? 3.25}
                onChange={(v) => updateDeduction("esic", { employerPercent: v })}
                min={0}
                max={100}
                step={0.01}
              />
              <NumInput
                label="Eligibility Ceiling"
                value={esic.eligibilityCeiling}
                onChange={(v) => updateDeduction("esic", { eligibilityCeiling: v })}
                min={0}
                step={1000}
              />
            </DeductionToggle>
            <DeductionToggle
              label="Professional Tax (PT)"
              enabled={pt.enabled}
              onToggle={handleTogglePt}
              tooltip="Professional Tax: A state-level tax levied on salaried employees, capped at a maximum of ₹2,500 per year."
            >
              <p
                className="m-0"
                style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontStyle: "italic" }}
              >
                Uses global PT slab table configured in Settings.
              </p>
            </DeductionToggle>
            <DeductionToggle
              label="TDS (Income Tax)"
              enabled={tds.enabled}
              onToggle={handleToggleTds}
              tooltip="Tax Deducted at Source: Income tax deducted by the employer from the employee's salary and deposited directly with the government."
            >
              <p
                className="m-0"
                style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontStyle: "italic" }}
              >
                Uses global TDS slab table configured in Settings.
              </p>
            </DeductionToggle>
            <div className="tb-custom-deductions-header">
              <span className="fs-7 fw-600 tb-custom-deductions-title">Custom Deductions</span>
              <BaseButton color="primary" onClick={handleAddCustomDeduction} size="sm">
                <Plus size={12} strokeWidth={2.5} /> Add Custom
              </BaseButton>
            </div>
            {custom.length === 0 ? (
              <p className="fs-7 m-0 tb-custom-deductions-empty">No custom deductions added.</p>
            ) : (
              custom.map((ded) => (
                <div key={ded.id} className="tb-custom-deductions-item">
                  <BaseInput
                    type="text"
                    placeholder="Deduction name"
                    value={ded.name}
                    handleChange={(e) =>
                      handleUpdateCustomDeduction(ded.id, "name", e.target.value)
                    }
                    name={`deduction-name-${ded.id}`}
                    className="tb-custom-deductions-input"
                  />
                  <div style={{ width: 140 }}>
                    <BaseSelect
                      name={`deduction-calculationType-${ded.id}`}
                      value={ded.calculationType}
                      handleChange={(name, val) =>
                        handleUpdateCustomDeduction(ded.id, "calculationType", val)
                      }
                      options={[
                        { value: "fixed", label: "Fixed" },
                        { value: "percentage_of_gross", label: "% of Gross" },
                      ]}
                      mb="mb-0"
                    />
                  </div>
                  {ded.calculationType === "fixed" ? (
                    <BaseInput
                      type="number"
                      placeholder="Amount"
                      value={ded.fixedAmount ?? ""}
                      handleChange={(e) =>
                        handleUpdateCustomDeduction(ded.id, "fixedAmount", Number(e.target.value))
                      }
                      name={`deduction-amount-${ded.id}`}
                      className="tb-custom-deductions-amount"
                    />
                  ) : (
                    <BaseInput
                      type="number"
                      placeholder="%"
                      value={ded.percentage ?? ""}
                      handleChange={(e) =>
                        handleUpdateCustomDeduction(ded.id, "percentage", Number(e.target.value))
                      }
                      name={`deduction-percent-${ded.id}`}
                      className="tb-custom-deductions-percent"
                    />
                  )}
                  <BaseButton
                    color="danger"
                    size="sm"
                    onClick={() => handleRemoveCustomDeduction(ded.id)}
                    className="d-inline-flex align-items-center"
                  >
                    <Trash2 size={13} />
                  </BaseButton>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="preview-panel d-flex flex-column gap-4">
          <StructureSummaryCard
            components={earningComponents}
            referenceSalary={REFERENCE_SALARY}
            allocation={allocation}
          />
          <LivePayrollPreview
            template={{ earningComponents, deductionComponents }}
            ptSlabs={[]}
            tdsSlabs={DEFAULT_TDS_SLABS}
          />
          <ValidationSummary
            errors={errors}
            cycleError={cycleError}
            duplicateError={duplicateError}
            allocationError={allocationError}
          />
        </div>
      </div>

      <div className={`tb-action-footer ${isOnboarding ? "onboarding" : "default"}`}>
        {hasAnyError && (
          <span className="d-flex align-items-center gap-1 fs-7 tb-action-footer-warning">
            <Info size={13} strokeWidth={2.5} />
            Fix validation issues before publishing
          </span>
        )}
        <BaseButton
          color="secondary"
          onClick={handleValidate}
          disabled={isBusy || isValidating}
          loader={isValidating}
        >
          {isValidating ? "Validating..." : "Validate Structure"}
        </BaseButton>
        <BaseButton color="cancel" onClick={handleSaveDraft} disabled={isBusy} loader={isSaving}>
          {isSaving ? PAYROLL_STRINGS.LOADING_SAVING : PAYROLL_STRINGS.BTN_SAVE_DRAFT}
        </BaseButton>
        <BaseButton
          color="primary"
          onClick={handlePublish}
          disabled={isBusy || !!cycleError || !!duplicateError}
          loader={isActivating}
        >
          {isActivating ? PAYROLL_STRINGS.LOADING_SAVING : PAYROLL_STRINGS.BTN_SAVE_ACTIVATE}
        </BaseButton>
      </div>

      <BaseModal
        isOpen={showUsedWarning}
        toggler={() => setShowUsedWarning(false)}
        title="Template in Use"
        submit={handleConfirmUsedWarning}
        submitText={
          pendingActivate ? PAYROLL_STRINGS.BTN_SAVE_ACTIVATE : PAYROLL_STRINGS.BTN_SAVE_DRAFT
        }
        closeText={PAYROLL_STRINGS.BTN_CANCEL}
        size="sm"
      >
        <p style={{ color: "var(--text-primary)", margin: 0, fontSize: "0.9rem", lineHeight: 1.6 }}>
          {PAYROLL_STRINGS.CONFIRM_EDIT_USED_TEMPLATE}
        </p>
      </BaseModal>
    </div>
  );
};

export default TemplateBuilder;
